<template>
  <el-dialog
    :visible="visible"
    :show-close="true"
    :close-on-click-modal="false"
    width="640px"
    custom-class="manage-connections-dialog"
    @close="onClose"
    @update:visible="onUpdateVisible"
  >
    <div slot="title" class="manage-title">
      <span class="manage-title__icon" aria-hidden="true">
        <happily-icon name="zap" :size="16" />
      </span>
      <div class="manage-title__text">
        <div class="manage-title__primary">Connected apps</div>
        <div class="manage-title__sub">Apps powering your Key Result automations</div>
      </div>
    </div>

    <div class="manage-body">
      <el-tabs v-model="activeTab" class="manage-tabs">
        <el-tab-pane name="connections">
          <span slot="label" class="manage-tab-label">Connected apps</span>

          <div v-if="loading" class="manage-loading">
            <div class="skeleton-row" v-for="n in 3" :key="n" />
          </div>

          <div v-else-if="accounts.length === 0" class="manage-empty">
            <div class="empty-illo" aria-hidden="true">
              <happily-icon name="zap" :size="22" />
            </div>
            <div class="empty-title">No connected apps yet</div>
            <div class="empty-sub">Connect your sources here so the next Key Result automation picks them up without a detour. Connections live here for the whole company.</div>
            <el-button
              type="primary"
              class="empty-cta"
              :loading="connecting"
              @click="openConnectPicker"
            >
              <happily-icon name="zap" :size="14" />
              <span>Connect an app</span>
            </el-button>
            <button
              type="button"
              class="empty-refresh"
              :class="{ 'is-spinning': loading }"
              :disabled="loading"
              @click="refresh"
            >
              <happily-icon name="refresh" :size="12" />
              <span>{{ loading ? 'Refreshing…' : 'Refresh' }}</span>
            </button>
          </div>

          <div v-else>
            <div class="manage-list-head">
              <div class="manage-eyebrow aq-form-label">CONNECTED · {{ accounts.length }}</div>
              <div class="manage-list-head__actions">
                <el-tooltip content="Refresh the list" placement="top" :open-delay="350">
                  <el-button
                    size="mini"
                    class="manage-list-head__refresh"
                    :class="{ 'is-spinning': loading }"
                    :disabled="loading"
                    aria-label="Refresh connected apps"
                    @click="refresh"
                  >
                    <happily-icon name="refresh" :size="13" />
                  </el-button>
                </el-tooltip>
                <el-button
                  size="mini"
                  class="manage-list-head__add"
                  :loading="connecting"
                  @click="openConnectPicker"
                >
                  <happily-icon name="plus" :size="13" />
                  <span>Connect app</span>
                </el-button>
              </div>
            </div>

            <ul class="conn-list">
              <li
                v-for="account in accounts"
                :key="account.id"
                class="conn-row"
                :class="`conn-row--${statusClass(account.status)}`"
              >
                <div class="conn-row__mark">
                  <AutomationToolkitMark
                    :slug="account.toolkit_slug"
                    :name="toolkitName(account.toolkit_slug)"
                    :logo-url="logoFor(account.toolkit_slug)"
                    size="lg"
                  />
                </div>

                <div class="conn-row__main">
                  <div class="conn-row__line">
                    <span class="conn-row__name">{{ toolkitName(account.toolkit_slug) }}</span>
                    <span class="conn-row__pill" :class="`conn-row__pill--${statusClass(account.status)}`">
                      <span class="conn-row__dot" />
                      {{ statusLabel(account.status) }}
                    </span>
                  </div>
                  <div class="conn-row__meta">
                    <span class="conn-row__label">{{ account.display_label }}</span>
                    <span class="conn-row__sep">·</span>
                    <span>by {{ account.connected_by_email }}</span>
                    <span class="conn-row__sep">·</span>
                    <span>connected {{ formatDate(account.created_at) }}</span>
                  </div>
                </div>

                <div class="conn-row__actions">
                  <el-tooltip content="Re-run the OAuth flow with this app" placement="top" :open-delay="350">
                    <el-button
                      size="mini"
                      class="conn-row__btn conn-row__btn--reconnect"
                      :loading="busyId === account.id && busyKind === 'reconnect'"
                      @click="onReconnect(account)"
                    >
                      <happily-icon name="refresh" :size="13" />
                      <span>Reconnect</span>
                    </el-button>
                  </el-tooltip>
                  <el-tooltip content="Remove this connection. Saved automations using it will pause." placement="top" :open-delay="350">
                    <el-button
                      size="mini"
                      class="conn-row__btn conn-row__btn--disconnect"
                      :loading="busyId === account.id && busyKind === 'disconnect'"
                      @click="onDisconnect(account)"
                    >
                      <happily-icon name="x" :size="13" />
                      <span>Disconnect</span>
                    </el-button>
                  </el-tooltip>
                </div>
              </li>
            </ul>

            <div class="manage-foot-note">
              Disconnecting an app pauses every automation that depends on it. They resume the moment the app is re-connected.
            </div>
          </div>
        </el-tab-pane>

        <el-tab-pane name="activity">
          <span slot="label" class="manage-tab-label">
            Activity
            <span
              v-if="failureCount24h > 0"
              class="manage-tab-badge"
              :title="`${failureCount24h} sync ${failureCount24h === 1 ? 'failure' : 'failures'} in the last 24 hours`"
            >{{ failureCount24h }}</span>
          </span>

          <!-- Filter row -->
          <div class="activity-filters">
            <div class="activity-filter-segments">
              <button
                type="button"
                v-for="opt in statusOptions"
                :key="opt.value"
                class="activity-segment"
                :class="{ 'activity-segment--active': statusFilter === opt.value }"
                @click="setStatusFilter(opt.value)"
              >
                {{ opt.label }}
                <span v-if="opt.value === 'failures' && failureCount24h > 0" class="activity-segment__count">{{ failureCount24h }}</span>
              </button>
            </div>
            <el-select
              v-model="toolkitFilter"
              size="mini"
              clearable
              placeholder="All apps"
              class="activity-toolkit-filter"
              popper-class="happily-dropdown-popper"
              @change="refreshActivity"
            >
              <el-option
                v-for="t in toolkitsInUse"
                :key="t.slug"
                :label="t.name"
                :value="t.slug"
              />
            </el-select>
          </div>

          <!-- Loading -->
          <div v-if="activityLoading" class="manage-loading">
            <div class="skeleton-row" v-for="n in 3" :key="n" />
          </div>

          <!-- Empty -->
          <div v-else-if="visibleRuns.length === 0" class="manage-empty">
            <div class="empty-illo" aria-hidden="true">
              <happily-icon name="clock" :size="22" />
            </div>
            <div class="empty-title">{{ emptyActivityTitle }}</div>
            <div class="empty-sub">{{ emptyActivitySub }}</div>
          </div>

          <!-- Feed -->
          <div v-else class="activity-feed">
            <div class="manage-eyebrow aq-form-label">SHOWING {{ visibleRuns.length }} OF {{ runs.length }}</div>
            <ul class="activity-list">
              <li
                v-for="run in visibleRuns"
                :key="run.id"
                class="activity-row"
                :class="`activity-row--${statusGroup(run.status)}`"
              >
                <div class="activity-row__mark">
                  <AutomationToolkitMark
                    :slug="run.toolkit_slug"
                    :name="toolkitName(run.toolkit_slug)"
                    :logo-url="logoFor(run.toolkit_slug)"
                    size="md"
                  />
                </div>
                <div class="activity-row__main">
                  <div class="activity-row__head">
                    <span
                      class="activity-row__pill"
                      :class="`activity-row__pill--${statusGroup(run.status)}`"
                    >
                      <span class="activity-row__dot" />
                      {{ runStatusLabel(run.status) }}
                    </span>
                    <span class="activity-row__title">{{ run.kr_title }}</span>
                  </div>
                  <div class="activity-row__detail" :class="{ 'activity-row__detail--error': run.status === 'error' || run.status === 'auth_expired' }">
                    {{ runDetailLine(run) }}
                  </div>
                  <div class="activity-row__meta">
                    <span>{{ toolkitName(run.toolkit_slug) }}</span>
                    <span class="activity-row__sep">·</span>
                    <span>{{ run.goal_title }}</span>
                    <span class="activity-row__sep">·</span>
                    <span>{{ runTriggerLabel(run.trigger) }}</span>
                  </div>
                </div>
                <div class="activity-row__when">
                  <div class="activity-row__time">{{ formatRelative(run.started_at) }}</div>
                  <div class="activity-row__duration">{{ formatDuration(run.duration_ms) }}</div>
                </div>
              </li>
            </ul>
            <div v-if="visibleRuns.length < runs.length" class="activity-show-more">
              <el-button class="activity-show-more__btn" size="mini" @click="visibleLimit += 10">Show 10 more</el-button>
            </div>
          </div>
        </el-tab-pane>
      </el-tabs>
    </div>

    <div slot="footer" class="manage-footer">
      <el-button class="manage-footer__close" @click="onClose">Close</el-button>
    </div>

    <AutomationToolkitBrowser
      :visible="connectPickerVisible"
      @pick="onToolkitPicked"
      @close="connectPickerVisible = false"
    />
  </el-dialog>
</template>

<script>
import AutomationToolkitMark from './AutomationToolkitMark.vue'
import AutomationToolkitBrowser from './AutomationToolkitBrowser.vue'
import { toolkitLogoUrl } from '@/services/krAutomationDemoBackend'
import {
  isKrAutomationDemoMode,
  completeDemoConnection,
  listSyncRuns,
} from '@/services/metricSourceService'

const TOOLKIT_NAMES = {
  hubspot: 'HubSpot',
  salesforce: 'Salesforce',
  stripe: 'Stripe',
  linear: 'Linear',
  notion: 'Notion',
  github: 'GitHub',
  slack: 'Slack',
  zendesk: 'Zendesk',
  intercom: 'Intercom',
  mixpanel: 'Mixpanel',
  google_analytics: 'Google Analytics',
  google_sheets: 'Google Sheets',
  jira: 'Jira',
  pipedrive: 'Pipedrive',
  close: 'Close',
  chargebee: 'Chargebee',
  amplitude: 'Amplitude',
  segment: 'Segment',
  monday: 'Monday',
  teams: 'Microsoft Teams',
}

export default {
  name: 'ManageConnectedAppsDialog',
  components: { AutomationToolkitMark, AutomationToolkitBrowser },
  props: {
    visible: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: false,
      accounts: [],
      busyId: null,
      busyKind: null,
      connectPickerVisible: false,
      connecting: false,
      // Tabs
      activeTab: 'connections',
      // Activity feed
      activityLoading: false,
      runs: [],
      failureCount24h: 0,
      statusFilter: 'all',          // all | success | no_change | failures
      toolkitFilter: '',
      visibleLimit: 10,
      statusOptions: [
        { value: 'all',       label: 'All' },
        { value: 'success',   label: 'Successes' },
        { value: 'no_change', label: 'No change' },
        { value: 'failures',  label: 'Failures' },
      ],
    }
  },
  computed: {
    visibleRuns() {
      return this.runs.slice(0, this.visibleLimit)
    },
    toolkitsInUse() {
      const seen = new Set()
      const out = []
      this.runs.forEach((r) => {
        if (!seen.has(r.toolkit_slug)) {
          seen.add(r.toolkit_slug)
          out.push({ slug: r.toolkit_slug, name: this.toolkitName(r.toolkit_slug) })
        }
      })
      return out.sort((a, b) => a.name.localeCompare(b.name))
    },
    emptyActivityTitle() {
      if (this.toolkitFilter || this.statusFilter !== 'all') return 'No activity matches these filters'
      return 'No automation activity yet'
    },
    emptyActivitySub() {
      if (this.toolkitFilter || this.statusFilter !== 'all') return 'Try widening the filter, or switch back to All to see every recent sync.'
      return 'When a Key Result automation runs, the result lands here so you can see exactly what happened.'
    },
  },
  watch: {
    visible(open) {
      if (open) {
        this.refresh()
        this.refreshActivity()
      }
    },
    activeTab(tab) {
      if (tab === 'activity' && this.runs.length === 0 && !this.activityLoading) {
        this.refreshActivity()
      }
    },
  },
  methods: {
    async refresh() {
      this.loading = true
      try {
        const result = await this.$store.dispatch('goals/listConnectedAccounts', { toolkitSlug: undefined })
        const list = (result && result.accounts) || []
        // Stable sort: active first, then by toolkit name, then by created_at desc
        const order = { active: 0, expired: 1, error: 2, revoked: 3 }
        list.sort((a, b) => {
          const oa = order[a.status] != null ? order[a.status] : 99
          const ob = order[b.status] != null ? order[b.status] : 99
          if (oa !== ob) return oa - ob
          const na = this.toolkitName(a.toolkit_slug)
          const nb = this.toolkitName(b.toolkit_slug)
          if (na !== nb) return na.localeCompare(nb)
          return (b.created_at || '').localeCompare(a.created_at || '')
        })
        this.accounts = list
      } finally {
        this.loading = false
      }
    },
    toolkitName(slug) {
      const k = (slug || '').toLowerCase()
      if (TOOLKIT_NAMES[k]) return TOOLKIT_NAMES[k]
      if (!k) return 'Unknown source'
      return k.charAt(0).toUpperCase() + k.slice(1).replace(/_/g, ' ')
    },
    logoFor(slug) {
      return toolkitLogoUrl(slug) || ''
    },
    statusClass(status) {
      if (status === 'active') return 'active'
      if (status === 'expired') return 'expired'
      if (status === 'error') return 'error'
      return 'inactive'
    },
    statusLabel(status) {
      if (status === 'active') return 'Connected'
      if (status === 'expired') return 'Reconnect needed'
      if (status === 'error') return 'Error'
      if (status === 'revoked') return 'Disconnected'
      return status
    },
    formatDate(iso) {
      if (!iso) return ''
      try {
        const d = new Date(iso)
        if (Number.isNaN(d.getTime())) return ''
        const now = new Date()
        const sameYear = d.getFullYear() === now.getFullYear()
        const opts = sameYear
          ? { month: 'short', day: 'numeric' }
          : { month: 'short', day: 'numeric', year: 'numeric' }
        return d.toLocaleDateString(undefined, opts)
      } catch {
        return ''
      }
    },
    async onReconnect(account) {
      this.busyId = account.id
      this.busyKind = 'reconnect'
      try {
        if (isKrAutomationDemoMode()) {
          // Demo path: revoke the existing one then issue a fresh active connection.
          await this.$store.dispatch('goals/revokeConnectedAccount', { accountId: account.id })
          await completeDemoConnection(account.toolkit_slug)
          await this.refresh()
          this.$message({ message: `${this.toolkitName(account.toolkit_slug)} reconnected.`, type: 'success' })
          return
        }
        const result = await this.$store.dispatch('goals/startConnectAccount', { toolkitSlug: account.toolkit_slug })
        if (result.success && result.redirect_url) {
          window.open(result.redirect_url, '_blank')
          this.$message({ message: `Finish reconnecting ${this.toolkitName(account.toolkit_slug)} in the new tab.`, type: 'info' })
        } else {
          this.$message.error('Could not start the reconnection. Try again.')
        }
      } finally {
        this.busyId = null
        this.busyKind = null
      }
    },
    async onDisconnect(account) {
      const name = this.toolkitName(account.toolkit_slug)
      try {
        await this.$confirm(
          `Any Key Result automation pulling from <strong>${name}</strong> will pause until you reconnect. Continue?`,
          'Disconnect this app?',
          {
            confirmButtonText: 'Disconnect',
            cancelButtonText: 'Cancel',
            type: 'warning',
            dangerouslyUseHTMLString: true,
            customClass: 'manage-confirm-box',
          }
        )
      } catch {
        return
      }
      this.busyId = account.id
      this.busyKind = 'disconnect'
      try {
        const result = await this.$store.dispatch('goals/revokeConnectedAccount', { accountId: account.id })
        if (result && result.success) {
          this.$message({ message: `${name} disconnected.`, type: 'success' })
          await this.refresh()
        } else {
          this.$message.error('Could not disconnect this app. Try again.')
        }
      } finally {
        this.busyId = null
        this.busyKind = null
      }
    },
    onUpdateVisible(v) {
      this.$emit('update:visible', v)
    },
    onClose() {
      this.$emit('update:visible', false)
      this.$emit('close')
    },

    /* ---------- Add a new connection ---------- */
    openConnectPicker() {
      this.connectPickerVisible = true
    },
    async onToolkitPicked(toolkit) {
      this.connectPickerVisible = false
      if (!toolkit || !toolkit.slug) return

      // Happily is first-party; no OAuth flow exists. Surface that
      // explicitly rather than silently no-op'ing the click.
      if (toolkit.slug === 'happily') {
        this.$message({
          message: 'Happily is always available — no connection needed.',
          type: 'info',
        })
        return
      }

      this.connecting = true
      try {
        if (isKrAutomationDemoMode()) {
          await completeDemoConnection(toolkit.slug)
          await this.refresh()
          this.$message({
            message: `${this.toolkitName(toolkit.slug)} connected.`,
            type: 'success',
          })
          return
        }
        const result = await this.$store.dispatch(
          'goals/startConnectAccount',
          { toolkitSlug: toolkit.slug }
        )
        if (result && result.success && result.redirect_url) {
          window.open(result.redirect_url, '_blank')
          this.$message({
            message: `Finish connecting ${this.toolkitName(toolkit.slug)} in the new tab. We'll show it here when it's ready.`,
            type: 'info',
          })
          setTimeout(() => { this.refresh() }, 3500)
        } else {
          this.$message.error(`Could not start connecting ${this.toolkitName(toolkit.slug)}. Try again.`)
        }
      } finally {
        this.connecting = false
      }
    },

    /* ---------- Activity feed ---------- */
    async refreshActivity() {
      this.activityLoading = true
      try {
        const params = {}
        if (this.toolkitFilter) params.toolkit_slug = this.toolkitFilter
        if (this.statusFilter && this.statusFilter !== 'all') params.status = this.statusFilter
        const result = await listSyncRuns(params)
        this.runs = (result && result.runs) || []
        this.failureCount24h = (result && result.failure_count_24h) || 0
        this.visibleLimit = 10
      } catch (e) {
        this.runs = []
      } finally {
        this.activityLoading = false
      }
    },
    setStatusFilter(value) {
      this.statusFilter = value
      this.refreshActivity()
    },
    statusGroup(status) {
      if (status === 'success') return 'success'
      if (status === 'no_change') return 'no-change'
      if (status === 'zero_records') return 'no-change'
      if (status === 'auth_expired') return 'auth'
      if (status === 'error') return 'error'
      return 'no-change'
    },
    runStatusLabel(status) {
      switch (status) {
        case 'success':       return 'Synced'
        case 'no_change':     return 'No change'
        case 'zero_records':  return 'No records'
        case 'auth_expired':  return 'Auth expired'
        case 'error':         return 'Error'
        default:              return status
      }
    },
    runTriggerLabel(trigger) {
      if (trigger === 'manual') return 'manual run'
      if (trigger === 'preview') return 'preview'
      return 'scheduled'
    },
    runDetailLine(run) {
      if (run.error && (run.status === 'error' || run.status === 'auth_expired')) return run.error.message
      if (run.status === 'zero_records') return 'Source returned no records. The recipe may need a wider filter or date range.'
      if (run.status === 'no_change') {
        const v = this.formatValue(run.new_value, run.unit)
        return v ? `Held steady at ${v}.` : 'No change since the last refresh.'
      }
      if (run.generated_note) return run.generated_note
      const newV = this.formatValue(run.new_value, run.unit)
      return newV ? `Updated to ${newV}.` : 'Sync completed.'
    },
    formatValue(value, unit) {
      if (value == null) return ''
      const formatted = Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })
      if (!unit) return formatted
      // Currency-style units render to the left, plain units stay to the right.
      const u = unit.toUpperCase()
      if (u === 'USD' || u === 'THB' || u === 'EUR' || u === 'SGD' || u === 'GBP') return `${u} ${formatted}`
      return `${formatted} ${unit}`
    },
    formatRelative(iso) {
      if (!iso) return ''
      const ms = Date.now() - new Date(iso).getTime()
      if (Number.isNaN(ms)) return ''
      const sec = Math.round(ms / 1000)
      if (sec < 60) return 'just now'
      const min = Math.round(sec / 60)
      if (min < 60) return `${min}m ago`
      const hr = Math.round(min / 60)
      if (hr < 24) return `${hr}h ago`
      const day = Math.round(hr / 24)
      if (day < 7) return `${day}d ago`
      return this.formatDate(iso)
    },
    formatDuration(ms) {
      if (!ms || ms < 0) return ''
      if (ms < 1000) return `${ms}ms`
      const s = ms / 1000
      if (s < 60) return `${s.toFixed(s < 10 ? 1 : 0)}s`
      const m = Math.floor(s / 60)
      const remS = Math.round(s - m * 60)
      return `${m}m ${remS}s`
    },
  },
}
</script>

<style scoped>
/* ===== Header ===== */
.manage-title {
  display: flex;
  align-items: center;
  gap: 14px;
}
.manage-title__icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f2ecfb;
  color: #6c30cc;
  flex-shrink: 0;
}
.manage-title__primary {
  font-family: 'Lato', sans-serif;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.005em;
  color: #1f1f1f;
  line-height: 1.2;
}
.manage-title__sub {
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.4;
  color: #8a8a8a;
  margin-top: 4px;
}

/* ===== Body ===== */
.manage-body {
  padding: 4px 4px 0;
  word-break: normal;
  overflow-wrap: break-word;
}
.manage-body * {
  word-break: normal;
  overflow-wrap: break-word;
}
.manage-eyebrow {
  margin: 0 0 12px;
}

/* ===== List ===== */
.conn-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.conn-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  background: #ffffff;
  border: 1px solid #e2e4ea;
  border-radius: 12px;
  transition: border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease;
}
.conn-row:hover {
  border-color: #d8c6f0;
  box-shadow: 0 2px 10px rgba(108, 48, 204, 0.06);
}

.conn-row__mark { flex-shrink: 0; }

.conn-row__main {
  flex: 1;
  min-width: 0;
}
.conn-row__line {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.conn-row__name {
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  font-weight: 800;
  line-height: 1.3;
  color: #1f1f1f;
  letter-spacing: -0.005em;
}
.conn-row__pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 8px 2px 7px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: #f2f3f5;
  color: #6f6f6f;
  border: 1px solid #e6e7ec;
}
.conn-row__dot {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: currentColor;
  display: inline-block;
}
.conn-row__pill--active   { background: #ecf8f1; color: #1f8b5a; border-color: #c8ecd6; }
.conn-row__pill--expired  { background: #fff7e4; color: #a4720a; border-color: #f5dfa2; }
.conn-row__pill--error    { background: #fdecec; color: #b22a2a; border-color: #f2c4c4; }
.conn-row__pill--inactive { background: #f2f3f5; color: #6f6f6f; border-color: #e6e7ec; }

.conn-row__meta {
  margin-top: 6px;
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: #8a8a8a;
  font-weight: 500;
  line-height: 1.45;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}
.conn-row__label {
  color: #4a4a4a;
  font-weight: 600;
}
.conn-row__sep {
  color: #c9ccd4;
}

.conn-row__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.conn-row__btn {
  font-family: 'Lato', sans-serif;
  font-weight: 700;
  font-size: 12px;
  border-radius: 8px;
  height: 32px;
  padding: 0 14px;
  border: 1px solid #e2e4ea;
  background: #ffffff;
  color: #4a4a4a;
  transition: background 140ms ease, color 140ms ease,
              border-color 140ms ease, box-shadow 140ms ease,
              transform 140ms ease;
}
.conn-row__btn:hover {
  background: #f2ecfb;
  color: #6c30cc;
  border-color: #d8c6f0;
  box-shadow: 0 1px 4px rgba(108, 48, 204, 0.08);
  transform: translateY(-0.5px);
}
.conn-row__btn:active {
  transform: translateY(0);
  box-shadow: none;
}
.conn-row__btn--disconnect:hover {
  background: #fdecec;
  color: #b22a2a;
  border-color: #f2c4c4;
  box-shadow: 0 1px 4px rgba(204, 48, 48, 0.08);
}

/* ===== Loading skeleton ===== */
.manage-loading {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.skeleton-row {
  height: 64px;
  border-radius: 12px;
  background: linear-gradient(90deg, #f5f5f8 0%, #eef0f5 50%, #f5f5f8 100%);
  background-size: 200% 100%;
  animation: shimmer 1.4s linear infinite;
  border: 1px solid #eef0f5;
}
@keyframes shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ===== Empty state ===== */
.manage-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 36px 24px 12px;
}
.empty-illo {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: #f2ecfb;
  color: #6c30cc;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
}
.empty-title {
  font-family: 'Lato', sans-serif;
  font-size: 15px;
  font-weight: 800;
  color: #1f1f1f;
  margin-bottom: 6px;
  letter-spacing: -0.005em;
}
.empty-sub {
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: #777;
  font-weight: 500;
  max-width: 380px;
  line-height: 1.5;
  word-break: normal;
  overflow-wrap: break-word;
}
.empty-cta {
  margin-top: 18px;
  height: 36px;
  border-radius: 8px;
  padding: 0 18px;
  font-family: 'Lato', sans-serif;
  font-weight: 700;
  font-size: 13px;
  background-color: #6c30cc;
  border-color: #6c30cc;
  color: #ffffff;
  transition: background-color 140ms ease, border-color 140ms ease,
              box-shadow 140ms ease, transform 140ms ease;
}
.empty-cta:hover,
.empty-cta:focus {
  background-color: #522698;
  border-color: #522698;
  color: #ffffff;
  box-shadow: 0 2px 8px rgba(108, 48, 204, 0.18);
  transform: translateY(-0.5px);
}
.empty-cta:active {
  transform: translateY(0);
  box-shadow: none;
}
.empty-refresh {
  margin-top: 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: 0;
  padding: 6px 10px;
  border-radius: 6px;
  font-family: 'Lato', sans-serif;
  font-weight: 600;
  font-size: 12px;
  color: #6f6f6f;
  cursor: pointer;
  transition: color 140ms ease, background 140ms ease;
}
.empty-refresh:hover {
  color: #6c30cc;
  background: #f2ecfb;
}
.empty-refresh[disabled] {
  cursor: default;
  opacity: 0.75;
}
.empty-refresh.is-spinning .happily-icon {
  animation: manage-refresh-spin 0.9s linear infinite;
}

/* ===== List head (eyebrow + add button) ===== */
.manage-list-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}
.manage-list-head .manage-eyebrow {
  margin: 0;
}
.manage-list-head__actions {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.manage-list-head__refresh {
  font-family: 'Lato', sans-serif;
  font-weight: 700;
  font-size: 12px;
  border-radius: 8px;
  height: 30px;
  width: 30px;
  padding: 0;
  border: 1px solid #e2e4ea;
  background: #ffffff;
  color: #4a4a4a;
  transition: background 140ms ease, color 140ms ease,
              border-color 140ms ease, box-shadow 140ms ease;
}
.manage-list-head__refresh:hover {
  background: #f2ecfb;
  color: #6c30cc;
  border-color: #d8c6f0;
  box-shadow: 0 1px 4px rgba(108, 48, 204, 0.08);
}
.manage-list-head__refresh.is-spinning .happily-icon {
  animation: manage-refresh-spin 0.9s linear infinite;
}
.manage-list-head__add {
  font-family: 'Lato', sans-serif;
  font-weight: 700;
  font-size: 12px;
  border-radius: 8px;
  height: 30px;
  padding: 0 12px;
  border: 1px solid #e2e4ea;
  background: #ffffff;
  color: #4a4a4a;
  transition: background 140ms ease, color 140ms ease,
              border-color 140ms ease, box-shadow 140ms ease;
}
.manage-list-head__add:hover {
  background: #f2ecfb;
  color: #6c30cc;
  border-color: #d8c6f0;
  box-shadow: 0 1px 4px rgba(108, 48, 204, 0.08);
}
@keyframes manage-refresh-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

/* ===== Footer ===== */
.manage-footer {
  display: flex;
  justify-content: flex-end;
}
.manage-footer__close {
  height: 32px;
  border-radius: 8px;
  font-weight: 700;
  font-family: 'Lato', sans-serif;
  border: 1px solid #e2e4ea;
  background: #ffffff;
  color: #4a4a4a;
}
.manage-footer__close:hover {
  background: #fafbfc;
  border-color: #d8dadd;
}

/* ===== Foot note ===== */
.manage-foot-note {
  margin-top: 16px;
  padding-bottom: 4px;
  font-family: 'Lato', sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: #8a8a8a;
  line-height: 1.55;
}

/* ===== Activity tab ===== */
.activity-filters {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 2px 0 14px;
  border-bottom: 1px solid #eef0f5;
  margin-bottom: 16px;
}
.activity-filter-segments {
  display: inline-flex;
  align-items: center;
  background: #fafbfc;
  border: 1px solid #e2e4ea;
  border-radius: 999px;
  padding: 3px;
  gap: 2px;
}
.activity-segment {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: 0;
  padding: 5px 12px;
  border-radius: 999px;
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  color: #6f6f6f;
  cursor: pointer;
  transition: background 140ms ease, color 140ms ease;
}
.activity-segment:hover {
  color: #1f1f1f;
}
.activity-segment--active {
  background: #ffffff;
  color: #6c30cc;
  box-shadow: 0 1px 2px rgba(31, 31, 31, 0.06);
}
.activity-segment__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  font-size: 10px;
  font-weight: 800;
  color: #b22a2a;
  background: #fdecec;
  border-radius: 999px;
  border: 1px solid #f2c4c4;
}
.activity-toolkit-filter {
  width: 160px;
}

.activity-feed {
  margin-top: 0;
}
.activity-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.activity-row {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 12px 14px;
  background: #ffffff;
  border: 1px solid #eef0f5;
  border-radius: 10px;
  transition: border-color 160ms ease, background-color 160ms ease;
}
.activity-row:hover {
  border-color: #e2e4ea;
  background-color: #fafbfd;
}

.activity-row__mark {
  flex-shrink: 0;
  margin-top: 1px;
}
.activity-row__main {
  flex: 1;
  min-width: 0;
}
.activity-row__head {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  row-gap: 6px;
}
.activity-row__pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 8px 2px 7px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  background: #f2f3f5;
  color: #6f6f6f;
  border: 1px solid #e6e7ec;
  flex-shrink: 0;
}
.activity-row__dot {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: currentColor;
  display: inline-block;
}
.activity-row__pill--success   { background: #ecf8f1; color: #1f8b5a; border-color: #c8ecd6; }
.activity-row__pill--no-change { background: #f2f3f5; color: #6f6f6f; border-color: #e6e7ec; }
.activity-row__pill--auth      { background: #fff7e4; color: #a4720a; border-color: #f5dfa2; }
.activity-row__pill--error     { background: #fdecec; color: #b22a2a; border-color: #f2c4c4; }

.activity-row__title {
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  font-weight: 800;
  color: #1f1f1f;
  letter-spacing: -0.005em;
  line-height: 1.3;
}
.activity-row__detail {
  margin-top: 6px;
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: #4a4a4a;
  font-weight: 500;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
}
.activity-row__detail--error {
  color: #b22a2a;
}
.activity-row__meta {
  margin-top: 6px;
  font-family: 'Lato', sans-serif;
  font-size: 11px;
  color: #8a8a8a;
  font-weight: 600;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}
.activity-row__sep {
  color: #c9ccd4;
}

.activity-row__when {
  flex-shrink: 0;
  text-align: right;
  min-width: 76px;
  padding-top: 1px;
}
.activity-row__time {
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.3;
  color: #4a4a4a;
}
.activity-row__duration {
  margin-top: 3px;
  font-family: 'Lato', sans-serif;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.3;
  color: #8a8a8a;
}

.activity-show-more {
  display: flex;
  justify-content: center;
  margin-top: 18px;
}
.activity-show-more__btn {
  height: 32px;
  border-radius: 8px;
  font-family: 'Lato', sans-serif;
  font-weight: 700;
  font-size: 12px;
  padding: 0 16px;
  border: 1px solid #e2e4ea;
  background: #ffffff;
  color: #4a4a4a;
}
.activity-show-more__btn:hover {
  background: #f2ecfb;
  color: #6c30cc;
  border-color: #d8c6f0;
}

/* Tab label ornaments */
.manage-tab-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: 'Lato', sans-serif;
  font-weight: 700;
}
.manage-tab-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  font-size: 10px;
  font-weight: 800;
  color: #b22a2a;
  background: #fdecec;
  border-radius: 999px;
  border: 1px solid #f2c4c4;
}
</style>

<style>
/* Non-scoped: dialog chrome consistent with KeyResultAutomationDialog. */
.manage-connections-dialog .el-dialog__header {
  padding: 22px 28px 14px;
}
.manage-connections-dialog .el-dialog__body {
  padding: 6px 28px 16px;
}
.manage-connections-dialog .el-dialog__footer {
  padding: 18px 28px 20px;
  border-top: 1px solid #eef0f5;
}
.manage-connections-dialog .el-dialog__title {
  display: none; /* we render a custom title block via the slot */
}
.manage-connections-dialog .el-dialog__headerbtn {
  top: 22px;
  right: 22px;
}

/* Confirm dialog tone — nudge the danger affordance toward Happily's red, not Element teal. */
.manage-confirm-box .el-button--primary,
.manage-confirm-box .el-message-box__btns .el-button--primary {
  background-color: #cc3030 !important;
  border-color: #cc3030 !important;
  color: #ffffff !important;
}
.manage-confirm-box .el-button--primary:hover,
.manage-confirm-box .el-message-box__btns .el-button--primary:hover {
  background-color: #b22a2a !important;
  border-color: #b22a2a !important;
}

/* Tabs — brand purple instead of default blue, slimmer divider. */
.manage-connections-dialog .manage-tabs .el-tabs__header {
  margin: 0 0 16px !important;
}
.manage-connections-dialog .manage-tabs .el-tabs__nav-wrap::after {
  height: 1px;
  background-color: #eef0f5;
}
.manage-connections-dialog .manage-tabs .el-tabs__active-bar {
  background-color: #6c30cc !important;
  height: 2px;
}
.manage-connections-dialog .manage-tabs .el-tabs__item {
  font-family: 'Lato', sans-serif;
  font-weight: 700;
  font-size: 13px;
  color: #6f6f6f;
  padding: 0 18px;
  height: 40px;
  line-height: 40px;
}
.manage-connections-dialog .manage-tabs .el-tabs__item:hover {
  color: #1f1f1f;
}
.manage-connections-dialog .manage-tabs .el-tabs__item.is-active {
  color: #6c30cc !important;
}
.manage-connections-dialog .manage-tabs .el-tabs__item:focus.is-active.is-focus:not(:active) {
  box-shadow: none;
}

/* el-button wraps slot content in a single <span>; turn that span into the
   flex container so the icon and label get a real gap (the gap declared on
   the button itself never applies — it only has one direct child). */
.manage-connections-dialog .conn-row__btn,
.manage-connections-dialog .manage-footer__close,
.manage-connections-dialog .activity-show-more__btn,
.manage-connections-dialog .empty-cta,
.manage-connections-dialog .manage-list-head__add,
.manage-connections-dialog .manage-list-head__refresh {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.manage-connections-dialog .conn-row__btn > span,
.manage-connections-dialog .manage-footer__close > span,
.manage-connections-dialog .activity-show-more__btn > span,
.manage-connections-dialog .empty-cta > span,
.manage-connections-dialog .manage-list-head__add > span,
.manage-connections-dialog .manage-list-head__refresh > span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  line-height: 1;
}

/* Crisper icon presence inside the action buttons. */
.manage-connections-dialog .conn-row__btn .happily-icon {
  opacity: 0.85;
  transition: opacity 140ms ease, transform 140ms ease;
}
.manage-connections-dialog .conn-row__btn:hover .happily-icon {
  opacity: 1;
}
.manage-connections-dialog .conn-row__btn--reconnect:hover .happily-icon {
  transform: rotate(-25deg);
}
.manage-connections-dialog .conn-row__btn--disconnect:hover .happily-icon {
  transform: scale(1.08);
}

/* Loading state should keep the icon hidden (Element UI swaps in its own
   loading spinner before the slot). */
.manage-connections-dialog .conn-row__btn.is-loading .happily-icon {
  display: none;
}
</style>
